import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {useEffect, useState, useRef} from 'react';
import {createGuid} from '../../functions/utility';

export type onChangeReasons =
    | 'createOption'
    | 'selectOption'
    | 'removeOption'
    | 'clear'
    | 'blur';

export type onChangeCallback<T> = (
    event: React.SyntheticEvent,
    value: T[],
    reason: onChangeReasons,
    details?: {option: T}
) => void;

export type onChangeCallbackSingle<T> = (
    event: React.SyntheticEvent,
    value: string | NonNullable<T>,
    reason: onChangeReasons,
    details?: {option: T}
) => void;

interface SSSingleSelectProps<T> {
    id?: string;
    label?: React.ReactNode | string;
    className?: string;
    value?: string | NonNullable<T>;
    addValueToOptionsIfMissing?: boolean;
    labelProperty?: string;
    keyProperty?: string;
    options?: T[];
    onChange?: onChangeCallbackSingle<T>;
    searchSelector?(option: T, searchText: string): boolean;
    getOptionLabel?(option: T): string;
    getOptionKey?(option: T): any;
    disabled?: boolean;
    [key: string]: any;
}

interface SSMultiSelectProps<T> {
    id?: string;
    label?: React.ReactNode | string;
    className?: string;
    value: T[] | null;
    options: T[];
    onChange: onChangeCallback<T>;
    searchSelector?(option: T, searchText: string): boolean;
    getOptionLabel?(option: T): string;
    getOptionKey?(option: T): any;
}

export default function PLMultiSelect<T>({
    id,
    value,
    options,
    label,
    className,
    onChange,
    searchSelector = () => true,
    getOptionLabel = (o) => o?.toString(),
    getOptionKey = (o) => o,
}: SSMultiSelectProps<T>): JSX.Element {
    return (
        <Autocomplete
            multiple
            size="small"
            id={id || createGuid()}
            options={options}
            getOptionLabel={getOptionLabel}
            value={value || []}
            filterOptions={(options, s) => {
                const reg = new RegExp(s.inputValue, 'i');
                return options.filter((x) => {
                    return (
                        !value?.some(
                            (y) => getOptionKey(y) === getOptionKey(x)
                        ) &&
                        (!s.inputValue || reg.test(getOptionLabel(x)))
                    );
                });
            }}
            onChange={onChange}
            renderInput={(params) => <TextField label={label} {...params} />}
            className={className}
            isOptionEqualToValue={(option: T, value: T) =>
                getOptionKey(option) === getOptionKey(value)
            }
        />
    );
}

export function PLSingleSelect<T>({
    id,
    value,
    options,
    label,
    className,
    onChange,
    searchSelector = () => true,
    getOptionLabel = (o) => o?.toString(),
    getOptionKey = (o) => o,
    disabled,
    addValueToOptionsIfMissing = false,
    labelProperty,
    keyProperty,
    ...rest
}: SSSingleSelectProps<T>): JSX.Element {
    const [iv, setIv] = useState<T>();
    const [io, setIo] = useState<T[]>();
    const valueIsT = useRef(false);
    useEffect(() => {
        if (!!options) {
            setIo(options);
        }

        if (typeof value === 'string') {
            if (!!options) {
                const o = options?.find((x) => getOptionKey(x) === value);

                if (addValueToOptionsIfMissing) {
                    if (!o) {
                        const i = {
                            [keyProperty]: value,
                            [labelProperty]: value,
                        };
                        //@ts-ignore
                        setIo([...options, i]);
                    }
                }
                //@ts-ignore
                setIv(o);
            } else {
                if (addValueToOptionsIfMissing) {
                    const i = {[keyProperty]: value, [labelProperty]: value};
                    //@ts-ignore
                    setIo([i]);
                    //@ts-ignore
                    setIv(i);
                } else {
                    //@ts-ignore
                    setIv(value);
                }
            }
        } else {
            if (addValueToOptionsIfMissing) {
                if (!!options) {
                    const o = options?.find((x) => x === value);
                    if (!o) {
                        //@ts-ignore
                        setIo([...options, value]);
                    }
                } else {
                    setIo([value]);
                }
            }
            //@ts-ignore
            setIv(value);
            valueIsT.current = true;
        }
    }, [value, options]);

    const oc = (event, value, reason, details) => {
        if (onChange) {
            if (valueIsT.current) {
                onChange(event, value, reason, details);
            } else {
                onChange(event, getOptionKey(value), reason, details);
            }
        }
    };

    return (
        <Autocomplete
            size="small"
            disabled={disabled}
            id={id || createGuid()}
            options={io || []}
            getOptionLabel={getOptionLabel}
            value={iv ?? null}
            filterOptions={(options, s) => {
                const reg = new RegExp(s.inputValue, 'i');
                return options.filter((x) => {
                    return !s.inputValue || reg.test(getOptionLabel(x));
                });
            }}
            onChange={oc}
            renderInput={(params) => <TextField label={label} {...params} />}
            className={className}
            isOptionEqualToValue={(option: T, value: T) =>
                getOptionKey(option) === getOptionKey(value)
            }
            {...rest}
        />
    );
}
